import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { NAMESPACES, resources, type LocaleResources } from './resources';

/** Languages supported by the frontoffice application. */
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'ar'] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS: 'common',
    detection: {
      caches: ['localStorage'],
      order: ['localStorage', 'navigator'],
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    ns: [...NAMESPACES],
    resources,
    supportedLngs: [...SUPPORTED_LANGUAGES],
  });

/** Narrows what Vite hands an HMR callback (an index-signature module namespace) to the resources export. */
function isLocaleResources(value: unknown): value is LocaleResources {
  return typeof value === 'object' && value !== null && 'en' in value && 'fr' in value && 'ar' in value;
}

/**
 * Pushes hot-swapped locale files into the running i18next instance.
 *
 * `init` copies `resources` into i18next's own store, so re-evaluating `./resources` leaves the
 * instance serving the bundle it parsed at startup: an added or edited key keeps rendering as its
 * raw key path until the page is reloaded. Re-emitting `languageChanged` is what actually repaints —
 * react-i18next binds to that event, not to store writes (`bindI18nStore` defaults to none).
 *
 * A key DELETED from a file survives until a full reload: a deep merge cannot express a removal.
 */
function acceptResources(hotModule: Record<string, unknown> | undefined): void {
  const fresh = hotModule?.resources;
  if (!isLocaleResources(fresh)) {
    return;
  }

  for (const language of SUPPORTED_LANGUAGES) {
    for (const namespace of NAMESPACES) {
      i18n.addResourceBundle(language, namespace, fresh[language][namespace], true, true);
    }
  }
  void i18n.changeLanguage(i18n.language);
}

// Dev only: Vite substitutes `undefined` for `import.meta.hot` in a production build and this drops
// out. Editing any `locales/<lng>/<ns>/*.json` re-evaluates `./resources` (it globs them), and this
// accepts that update so the page repaints without a reload.
if (import.meta.hot) {
  import.meta.hot.accept('./resources', acceptResources);
}

export default i18n;
